import {
  IElement,
  PosKey,
  PositionMapping,
  createElementsInsideEmptyMarkers,
} from '@gohighlevel/ghl-proposals-common'

import { useFillableFieldsV2 } from './useFillableFieldsV2'

export function useMarkerForEditor() {
  // const isPrintAdjustToConsider = computed(() => {
  //   return !store.isInternal
  // })
  const isPrintAdjustToConsider = false
  const { getElementRefById } = useFillableFieldsV2()
  const getPosAtCoordsForFillables = (editor: any, element: IElement) => {
    if (!editor.value) return {} as PositionMapping
    const mapping: PositionMapping = {}

    // Helper function to safely get position
    const safeGetPosition = (left: number, top: number) => {
      try {
        return editor.value.view.posAtCoords({ left, top })?.pos ?? null
      } catch (error) {
        return null
      }
    }

    // Get all fillable children from the text block
    const fillableElements = element.children || []

    fillableElements.forEach(fillable => {
      let quadrant = null
      const fillableElement = getElementRefById(fillable.id)
      if (fillableElement && editor.value) {
        const elementRect = fillableElement.getBoundingClientRect()
        const fillableElementParent = fillableElement.parentElement
        const fillableElementParentRect =
          fillableElementParent?.getBoundingClientRect() ?? null
        quadrant = getElementQuadrant(elementRect, fillableElementParentRect)

        // Get positions for all 6 corners using safe position getter
        const posFromLeftTop = safeGetPosition(
          elementRect.left,
          elementRect.top
        )
        const posFromLeftBottom = safeGetPosition(
          elementRect.left,
          elementRect.bottom
        )
        const posFromCenterTop = safeGetPosition(
          elementRect.left + elementRect.width / 2,
          elementRect.top
        )
        const posFromCenterBottom = safeGetPosition(
          elementRect.left + elementRect.width / 2,
          elementRect.bottom
        )
        const posFromCenterCenter = safeGetPosition(
          elementRect.left + elementRect.width / 2,
          elementRect.top + elementRect.height / 2
        )
        const posFromRightTop = safeGetPosition(
          elementRect.right,
          elementRect.top
        )
        const posFromRightBottom = safeGetPosition(
          elementRect.right,
          elementRect.bottom
        )

        let nodeDom = null
        let markerCoordsForNodeDom = null

        // Safely get DOM position and coordinates
        if (posFromCenterTop !== null) {
          try {
            nodeDom = editor.value.view.domAtPos(posFromCenterTop)
            if (nodeDom) {
              markerCoordsForNodeDom = editor.value.view.coordsAtPos(
                nodeDom.offset
              )
            }
          } catch (error) {
            // Position could not be determined
          }
        }

        // Get coordinates for all positions
        const markerCoordsForLeftTop =
          editor.value.view.coordsAtPos(posFromLeftTop)
        const markerCoordsForLeftBottom =
          editor.value.view.coordsAtPos(posFromLeftBottom)
        const markerCoordsForCenterTop =
          editor.value.view.coordsAtPos(posFromCenterTop)
        const markerCoordsForCenterBottom =
          editor.value.view.coordsAtPos(posFromCenterBottom)
        const markerCoordsForCenterCenter =
          editor.value.view.coordsAtPos(posFromCenterCenter)
        const markerCoordsForRightTop =
          editor.value.view.coordsAtPos(posFromRightTop)
        const markerCoordsForRightBottom =
          editor.value.view.coordsAtPos(posFromRightBottom)

        const posToConsiderKey = getPosToConsiderKey(
          posFromLeftTop,
          posFromLeftBottom,
          posFromCenterTop,
          posFromCenterBottom,
          posFromRightTop,
          posFromRightBottom,
          posFromCenterCenter,
          quadrant
        )

        const isAllPositionsSameBool = isAllPositionsSame(
          posFromLeftTop,
          posFromLeftBottom,
          posFromCenterTop,
          posFromCenterBottom,
          posFromRightTop,
          posFromRightBottom,
          posFromCenterCenter
        )

        mapping[fillable.id] = {
          editorInstanceId: editor.value.view.state.doc.attrs.id || 'default',
          markerPos: {
            posFromLeftTop: {
              pos: posFromLeftTop,
              relativeCoords: {
                top: elementRect.top - markerCoordsForLeftTop.top,
                bottom: elementRect.bottom - markerCoordsForLeftTop.top,
                left: elementRect.left - markerCoordsForLeftTop.left,
                right: elementRect.right - markerCoordsForLeftTop.left,
              },
              markerCoords: markerCoordsForLeftTop,
            },
            posFromLeftBottom: {
              pos: posFromLeftBottom,
              relativeCoords: {
                top: elementRect.top - markerCoordsForLeftBottom.top,
                bottom: elementRect.bottom - markerCoordsForLeftBottom.top,
                left: elementRect.left - markerCoordsForLeftBottom.left,
                right: elementRect.right - markerCoordsForLeftBottom.left,
              },
              markerCoords: markerCoordsForLeftBottom,
            },
            posFromCenterTop: {
              pos: posFromCenterTop,
              relativeCoords: {
                top: elementRect.top - markerCoordsForCenterTop.top,
                bottom: elementRect.bottom - markerCoordsForCenterTop.top,
                left: elementRect.left - markerCoordsForCenterTop.left,
                right: elementRect.right - markerCoordsForCenterTop.left,
              },
              markerCoords: markerCoordsForCenterTop,
            },
            posFromCenterBottom: {
              pos: posFromCenterBottom,
              relativeCoords: {
                top: elementRect.top - markerCoordsForCenterBottom.top,
                bottom: elementRect.bottom - markerCoordsForCenterBottom.top,
                left: elementRect.left - markerCoordsForCenterBottom.left,
                right: elementRect.right - markerCoordsForCenterBottom.left,
              },
              markerCoords: markerCoordsForCenterBottom,
            },
            posFromCenterCenter: {
              pos: posFromCenterCenter,
              relativeCoords: {
                top: elementRect.top - markerCoordsForCenterCenter.top,
                bottom: elementRect.bottom - markerCoordsForCenterCenter.top,
                left: elementRect.left - markerCoordsForCenterCenter.left,
                right: elementRect.right - markerCoordsForCenterCenter.left,
              },
              markerCoords: markerCoordsForCenterCenter,
            },
            posFromRightTop: {
              pos: posFromRightTop,
              relativeCoords: {
                top: elementRect.top - markerCoordsForRightTop.top,
                bottom: elementRect.bottom - markerCoordsForRightTop.top,
                left: elementRect.left - markerCoordsForRightTop.left,
                right: elementRect.right - markerCoordsForRightTop.left,
              },
              markerCoords: markerCoordsForRightTop,
            },
            posFromRightBottom: {
              pos: posFromRightBottom,
              relativeCoords: {
                top: elementRect.top - markerCoordsForRightBottom.top,
                bottom: elementRect.bottom - markerCoordsForRightBottom.top,
                left: elementRect.left - markerCoordsForRightBottom.left,
                right: elementRect.right - markerCoordsForRightBottom.left,
              },
              markerCoords: markerCoordsForRightBottom,
            },
            posFromNodeDom: {
              pos: nodeDom?.offset ?? null,
              relativeCoords: {
                top: elementRect.top - (markerCoordsForNodeDom?.top ?? 0),
                bottom: elementRect.bottom - (markerCoordsForNodeDom?.top ?? 0),
                left: elementRect.left - (markerCoordsForNodeDom?.left ?? 0),
                right: elementRect.right - (markerCoordsForNodeDom?.left ?? 0),
              },
              markerCoords: markerCoordsForNodeDom,
            },
          },
          elementRect,
          parentElementRect: fillableElementParentRect ?? null,
          posToConsiderKey,
          quadrant,
          isAllPositionsSame: isAllPositionsSameBool,
        }
      }
    })
    return mapping
  }

  const getPosToConsiderKey = (
    posFromLeftTop: number,
    posFromLeftBottom: number,
    posFromCenterTop: number,
    posFromCenterBottom: number,
    posFromRightTop: number,
    posFromRightBottom: number,
    posFromCenterCenter: number,
    quadrant: number | null
  ): PosKey => {
    const defaultPosKey = posFromCenterTop
      ? PosKey.posFromCenterTop
      : posFromCenterBottom
      ? PosKey.posFromCenterBottom
      : posFromLeftTop
      ? PosKey.posFromLeftTop
      : posFromLeftBottom
      ? PosKey.posFromLeftBottom
      : posFromRightTop
      ? PosKey.posFromRightTop
      : PosKey.posFromRightBottom
    if (quadrant) {
      switch (quadrant) {
        case 1:
          return posFromRightBottom ? PosKey.posFromRightBottom : defaultPosKey
        case 2:
          return posFromCenterBottom
            ? PosKey.posFromCenterBottom
            : defaultPosKey
        case 3:
          return posFromLeftBottom ? PosKey.posFromLeftBottom : defaultPosKey
        case 4:
          return posFromRightTop ? PosKey.posFromRightTop : defaultPosKey
        case 5:
          return posFromCenterCenter
            ? PosKey.posFromCenterCenter
            : defaultPosKey
        case 6:
          return posFromLeftTop ? PosKey.posFromLeftTop : defaultPosKey
        case 7:
          return posFromRightTop ? PosKey.posFromRightTop : defaultPosKey
        case 8:
          return posFromCenterTop ? PosKey.posFromCenterTop : defaultPosKey
        case 9:
          return posFromLeftTop ? PosKey.posFromLeftTop : defaultPosKey
        default:
          return defaultPosKey
      }
    }

    if (posFromLeftTop && posFromRightBottom && posFromCenterTop) {
      return PosKey.posFromCenterTop
    }

    // If all positions are available, prefer center positions
    if (posFromCenterTop && posFromCenterBottom) {
      return PosKey.posFromCenterTop
    }

    // If left positions are available
    if (posFromLeftTop && posFromLeftBottom) {
      return PosKey.posFromLeftTop
    }

    // If right positions are available
    if (posFromRightTop && posFromRightBottom) {
      return PosKey.posFromRightTop
    }

    // Fallback to any available position in priority order
    if (posFromCenterTop) return PosKey.posFromCenterTop
    if (posFromCenterBottom) return PosKey.posFromCenterBottom
    if (posFromLeftTop) return PosKey.posFromLeftTop
    if (posFromLeftBottom) return PosKey.posFromLeftBottom
    if (posFromRightTop) return PosKey.posFromRightTop
    if (posFromRightBottom) return PosKey.posFromRightBottom
    if (posFromCenterCenter) return PosKey.posFromCenterCenter

    return PosKey.posFromCenterTop // default fallback
  }

  const insertElementsInsideEmptyMarkers = (
    fillablePositionsMapping: PositionMapping,
    debugMode: boolean
  ) => {
    const elementsWithEmptyMarkers = Object.keys(fillablePositionsMapping).map(
      elemId => {
        return {
          id: elemId,
          value: fillablePositionsMapping[elemId],
        }
      }
    )
    createElementsInsideEmptyMarkers(
      elementsWithEmptyMarkers,
      debugMode,
      isPrintAdjustToConsider
    )
  }

  /**
   * Determines which quadrant of a 3x3 grid an element belongs to based on its position relative to its parent
   * Returns a number from 1-9 representing the quadrant:
   * 1 2 3
   * 4 5 6
   * 7 8 9
   * @param elementRect The bounding rectangle of the element
   * @param parentRect The bounding rectangle of the parent
   * @returns number from 1-9 indicating the quadrant
   */
  const getElementQuadrant = (
    elementRect: DOMRect,
    parentRect: DOMRect | null
  ): number | null => {
    if (!parentRect) return null
    // Calculate the width and height of each grid cell
    const cellWidth = parentRect.width / 3
    const cellHeight = parentRect.height / 3

    // Calculate the center point of the element
    const elementCenterX = elementRect.left + elementRect.width / 2
    const elementCenterY = elementRect.top + elementRect.height / 2

    // Calculate relative position to parent
    const relativeX = elementCenterX - parentRect.left
    const relativeY = elementCenterY - parentRect.top

    // Determine column (1-3)
    const col = Math.floor(relativeX / cellWidth)
    // Determine row (0-2)
    const row = Math.floor(relativeY / cellHeight)

    // Convert grid position to quadrant number (1-9)
    // Using row * 3 + col + 1 formula to convert 2D grid position to 1-9 number
    const quadrant = row * 3 + col + 1

    // Ensure the quadrant is within valid range
    return Math.max(1, Math.min(9, quadrant))
  }

  const isAllPositionsSame = (
    posFromLeftTop: number,
    posFromLeftBottom: number,
    posFromCenterTop: number,
    posFromCenterBottom: number,
    posFromRightTop: number,
    posFromRightBottom: number,
    posFromCenterCenter: number
  ) => {
    return (
      posFromCenterTop === posFromCenterBottom &&
      posFromCenterTop === posFromLeftTop &&
      posFromCenterTop === posFromLeftBottom &&
      posFromCenterTop === posFromRightTop &&
      posFromCenterTop === posFromRightBottom &&
      posFromCenterTop === posFromCenterCenter
    )
  }

  return {
    getPosAtCoordsForFillables,
    insertElementsInsideEmptyMarkers,
    isPrintAdjustToConsider: isPrintAdjustToConsider,
  }
}
